import { useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import useCyclePurchaseApi from "@apis/useCyclePurchaseApi";
import useValidationErrors from "@util/hook/useValidationErrors";
import { DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER } from "@icoach/router/routerPath";

/**
 * 循環訂單暫存提交 Hook
 * 負責處理循環訂單的暫存邏輯
 *
 * @param {Object} options - 設定選項
 * @param {Function} [options.onSuccess] - 成功後的回調（可選，會覆蓋預設行為）
 * @returns {Object} 提交函數和狀態
 */
const useCyclePurchaseTemporaryOrderSubmit = ({ onSuccess } = {}) => {
	const history = useHistory();
	const { postTemporaryCycleOrderApi } = useCyclePurchaseApi();
	const { validationErrors, clearValidationErrors } = useValidationErrors();
	const apiSendingRef = useRef(false);

	/**
	 * 提交暫存循環訂單
	 * @param {Object} params - 提交參數
	 * @param {number} cycleOrderID - 循環訂單ID
	 * @returns {Promise<boolean>} 是否成功
	 */
	const submit = useCallback(
		async (params, cycleOrderID = 0) => {
			if (apiSendingRef.current) return false;

			clearValidationErrors();
			apiSendingRef.current = true;

			try {
				const resp = await postTemporaryCycleOrderApi(params, cycleOrderID);

				if (resp) {
					// 成功
					if (onSuccess && typeof onSuccess === "function") {
						// 自訂成功處理
						onSuccess(resp, params);
					} else {
						// 預設成功處理：轉跳回列表頁
						history.replace(DOCUMENT_HEALTHY_CYCLE_PURCHASE_ROUTER.BASE);
					}

					apiSendingRef.current = false;
					return true;
				} else {
					// 失敗
					apiSendingRef.current = false;
					return false;
				}
			} catch (error) {
				console.error("提交暫存循環訂單失敗:", error);
				apiSendingRef.current = false;
				return false;
			}
		},
		// eslint-disable-next-line
		[postTemporaryCycleOrderApi, clearValidationErrors, onSuccess, history],
	);

	/**
	 * 檢查是否正在提交中
	 */
	const isSubmitting = useCallback(
		() => {
			return apiSendingRef.current;
		},
		// eslint-disable-next-line
		[],
	);

	return {
		submit,
		isSubmitting,
		validationErrors,
		clearValidationErrors,
	};
};

export default useCyclePurchaseTemporaryOrderSubmit;
